import React, { useMemo, useState, useEffect, useRef } from "react"
import { Container, Row, Col, CardBody, CardTitle } from "reactstrap"
import { useQuery } from "@tanstack/react-query"
import Slider from "react-slick"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import OptimizedImage from "../../components/Common/OptimizedImage"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import PlatformTabs from "../../components/Common/PlatformTabs"
import KTRLineChart from "../../additional_components/common/charts-graphs/KTRLineChart"
import SEO from "../../components/Common/SEO"
import './CustomInsightsDashboard.css'
import { Link, useNavigate } from "react-router-dom"
import axios from 'axios'
import { api } from "../../utils/variables"
import { queryKeys } from "../../utils/queryClient"
import { apiService } from "../../utils/prefetchManager"
import { usePerformanceMonitor } from "../../hooks/usePerformanceMonitor"
import { PAGE_SEO, STRUCTURED_DATA } from "../../config/seoConfig"
import { generateCanonicalUrl, getAppDetailsLink, generateSlug, applySEODate } from "../../utils/seoUtils"
import { useFilters } from "../../hooks/useFilters"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import { userProfileApi } from "../../services/userProfileApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"
import { usePlatforms } from "../../contexts/PlatformsContext"

// Platform pill row defaults to Meta Quest on first load (before the user has
// picked/saved a platform filter of their own).
const DEFAULT_PLATFORM = ["meta"]

// Reuse the same carousel settings as the dashboard for consistent visuals
const carouselSettings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 10,
  slidesToScroll: 1,
  autoplay: false,
  arrows: false,
  swipe: true,
  touchMove: true,
  centerMode: false,
  centerPadding: "0px",
  responsive: [
    { breakpoint: 1600, settings: { slidesToShow: 9 } },
    { breakpoint: 1400, settings: { slidesToShow: 8 } },
    { breakpoint: 1200, settings: { slidesToShow: 9 } },
    { breakpoint: 1024, settings: { slidesToShow: 8 } },
    { breakpoint: 840, settings: { slidesToShow: 7, dots: true } },
    { breakpoint: 768, settings: { slidesToShow: 3, dots: true } },
    { breakpoint: 676, settings: { slidesToShow: 2, dots: true } },
    { breakpoint: 480, settings: { slidesToShow: 1, dots: true } },
  ],
}

const CarouselComponent = React.memo(
  ({
    games,
    defaultOpenGraph,
    disableLink = false,
    onCardClick,
    selectedAppId,
    slidesToShowOverride,
    cardDimsOverride,
    showTrackButton = false,
    onTrack,
    // Used when the app data itself doesn't carry a platformId (this
    // endpoint doesn't return one per-app) — falls back to whichever single
    // platform the section's filter is currently scoped to. Left null when
    // multiple platforms are selected at once, since we can't tell which one
    // a given card actually belongs to.
    fallbackPlatformId,
  }) => {
    const { getPlatformIcon } = usePlatforms()
    const [windowWidth, setWindowWidth] = React.useState(
      typeof window !== "undefined" ? window.innerWidth : 0,
    )

    React.useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth)
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }, [])

    // Manual drag-distance tracking so a mouse/touch drag across the slider
    // never gets misread as a click on whichever card ends up under the
    // cursor at release (see the identical fix on the Weekly Insights
    // dashboard carousels for the full rationale).
    const dragRef = useRef({ down: false, startX: 0, startY: 0, moved: false })

    useEffect(() => {
      const getPoint = (e) => (e.touches ? e.touches[0] : e)
      const onMove = (e) => {
        if (!dragRef.current.down) return
        const { clientX, clientY } = getPoint(e)
        if (Math.abs(clientX - dragRef.current.startX) > 6 || Math.abs(clientY - dragRef.current.startY) > 6) {
          dragRef.current.moved = true
        }
      }
      const onUp = () => {
        dragRef.current.down = false
        if (dragRef.current.moved) setTimeout(() => { dragRef.current.moved = false }, 0)
      }
      window.addEventListener("mousemove", onMove)
      window.addEventListener("mouseup", onUp)
      window.addEventListener("touchmove", onMove)
      window.addEventListener("touchend", onUp)
      return () => {
        window.removeEventListener("mousemove", onMove)
        window.removeEventListener("mouseup", onUp)
        window.removeEventListener("touchmove", onMove)
        window.removeEventListener("touchend", onUp)
      }
    }, [])

    const handleSlidePointerDown = (e) => {
      const point = e.touches ? e.touches[0] : e
      dragRef.current = { down: true, startX: point.clientX, startY: point.clientY, moved: false }
    }

    const getCardDimensions = () => {
      if (cardDimsOverride) {
        if (windowWidth < 768 && cardDimsOverride.mobile) return cardDimsOverride.mobile
        if (cardDimsOverride.desktop) return cardDimsOverride.desktop
      }
      if (windowWidth < 768) {
        return { maxWidth: "220px", minWidth: "190px", aspectRatio: "4 / 3", padding: "10px" }
      }
      // Tablet / Desktop: the image fills the card's width (via aspectRatio)
      // instead of a small fixed-pixel icon floating in a much wider slide.
      return { maxWidth: "100%", minWidth: "100%", aspectRatio: "16 / 10", padding: "10px" }
    }

    const cardDims = getCardDimensions()

    if (!games || games.length === 0) {
      return (
        <div className="d-flex gap-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} style={{ minWidth: cardDims.minWidth }}>
              <Skeleton height={windowWidth < 768 ? 180 : 130} borderRadius={8} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />
              <Skeleton width="70%" height={16} style={{ marginTop: "12px" }} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />
            </div>
          ))}
        </div>
      )
    }

    const baseSlidesToShow = slidesToShowOverride || carouselSettings.slidesToShow || 6
    const desiredSlidesToShow = baseSlidesToShow
    const slidesToShow = Math.max(1, Math.min(desiredSlidesToShow, games.length || 1))

    const localSettings = {
      ...carouselSettings,
      slidesToShow,
      infinite: (games.length || 0) > slidesToShow,
      // When a caller pins slidesToShow via slidesToShowOverride (e.g. forcing a
      // single full-width slide on mobile to match its own oversized card dims),
      // drop the shared responsive breakpoints too — otherwise slick re-applies
      // carouselSettings.responsive's slidesToShow (2/3 at these widths) on
      // resize, right back on top of the override, and the fixed-width card
      // (min-width 260px) blows out a slide that's now only ~1/2 or ~1/3 as wide.
      ...(slidesToShowOverride ? { responsive: [] } : {}),
    }

    const getCardGap = () => {
      if (windowWidth < 768) return 12
      if (windowWidth < 1200) return 0.5
      return Math.min(1.5, 0.5 + (windowWidth - 1200) / 1000)
    }

    const cardGap = getCardGap()
    const cardGapHalf = cardGap / 2

    return (
      <Slider {...localSettings} dots={false}>
        {games.map((app, index) => {
          const isMobile = windowWidth < 768
          const cardMarkup = (
            <div
              style={{
                width: "100%",
                maxWidth: cardDims.maxWidth,
                minWidth: cardDims.minWidth,
                background: "#171821",
                border: "1px solid #353748",
                borderRadius: "14px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                margin: "auto",
                cursor: "pointer",
                padding: cardDims.padding,
                WebkitUserDrag: "none",
                userSelect: "none",
              }}
            >
              <div style={{ position: "relative", width: "100%", aspectRatio: cardDims.aspectRatio, flexShrink: 0 }}>
                <div style={{ width: "100%", height: "100%", overflow: "hidden", borderRadius: "10px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)" }}>
                  <OptimizedImage src={app.image_url || "https://via.placeholder.com/253x250/171821/ffffff?text=Loading..."} alt={`${app.name} Game Image`} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", pointerEvents: "none" }} lazy={true} quality={85} />
                </div>

                {/* Platform icon only (no label) — matches the Weekly Insights
                    carousel cards; there's no room for a text label at this
                    card size. Falls back to the section's active platform
                    filter when the app itself has no platformId. */}
                {(() => {
                  const iconPlatformId = app?.platformId || fallbackPlatformId
                  const platformIconUrl = iconPlatformId ? getPlatformIcon(iconPlatformId) : null
                  if (!platformIconUrl) return null
                  return (
                    <span
                      style={{
                        position: "absolute",
                        right: isMobile ? -8 : -10,
                        bottom: isMobile ? -8 : -10,
                        width: isMobile ? 32 : 28,
                        height: isMobile ? 32 : 28,
                        borderRadius: "50%",
                        background: "#171821",
                        border: "2px solid #171821",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.4)",
                        pointerEvents: "none",
                      }}
                    >
                      <img
                        src={platformIconUrl}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        style={{ width: "72%", height: "72%", objectFit: "contain", borderRadius: "2px" }}
                        onError={(e) => { e.target.parentElement.style.display = "none" }}
                      />
                    </span>
                  )
                })()}
              </div>

              <CardBody style={{ backgroundColor: "transparent", padding: "8px 2px 0", width: "100%" }}>
                {/* Single line, ellipsis-truncated — the native `title` attribute
                    shows the full name as a tooltip on hover instead of wrapping
                    the card to 2-3 lines. */}
                <CardTitle tag="h6" className="mb-0 trending_game_titleup" title={app.name} style={{ fontSize: isMobile ? "14px" : "13px", lineHeight: "1.35", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600, margin: 0, width: "100%" }}>
                  {app.name}
                </CardTitle>
              </CardBody>
              {showTrackButton && (
                (() => {
                  try {
                    const raw = typeof window !== 'undefined' ? sessionStorage.getItem('trackedApps') : null
                    const trackedArr = raw ? JSON.parse(raw) : []
                    const already = Array.isArray(trackedArr) && trackedArr.some(t => t && t.item_id === app.item_id)
                    if (already) return null
                  } catch (e) { /* ignore parse errors and show button */ }

                  return (
                    <div style={{ width: '100%', padding: isMobile ? '6px 0 2px' : '6px 0 0', display: 'flex', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onTrack && onTrack(app) }}
                        style={{
                          background: 'transparent',
                          color: '#F6255A',
                          border: '1px solid #F6255A',
                          padding: '4px 16px',
                          borderRadius: 999,
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: 12,
                          transition: 'background 0.15s ease, color 0.15s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#F6255A'; e.currentTarget.style.color = '#fff' }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#F6255A' }}
                      >
                        Track
                      </button>
                    </div>
                  )
                })()
              )}
            </div>
          )

          return (
            <div
              key={app.item_id || `${index}`}
              style={{ padding: `0 ${cardGapHalf}px`, boxSizing: "border-box" }}
              onMouseDown={handleSlidePointerDown}
              onTouchStart={handleSlidePointerDown}
            >
              {disableLink ? (
                <div
                  className={`card-link-wrapper ${selectedAppId === app.item_id ? "selected-app" : ""}`}
                  onClick={() => { if (!dragRef.current.moved) onCardClick && onCardClick(app) }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={e => { if (e.key === "Enter" || e.key === " ") onCardClick && onCardClick(app) }}
                >
                  {cardMarkup}
                </div>
              ) : (
                <a
                  href={getAppDetailsLink(app)}
                  className="card-link-wrapper"
                  target="_blank"
                  rel="noreferrer"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onClick={(e) => { if (dragRef.current.moved) e.preventDefault() }}
                >
                  {cardMarkup}
                </a>
              )}
            </div>
          )
        })}
      </Slider>
    )
  },
)

CarouselComponent.displayName = "CarouselComponent"

const getPageNumbers = (currentPage, totalPages) => {
  const pages = []
  for (let i = 1; i <= Math.min(3, totalPages); i++) pages.push(i)
  if (totalPages > 3) {
    if (currentPage > 3) {
      pages.push("...")
      pages.push(currentPage)
    } else pages.push("...")
  }
  return pages
}

const TopPerformerOnly = () => {
  const { measureOperation } = usePerformanceMonitor("TopPerformerOnly")
  const { filterSections } = useFilters()
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  // Initialize from cache to prevent flash - check localStorage first (persists across sessions)
  // If cached as premium (true), start as true to prevent blur flash
  // If cached as explorer (false), start as false to show blur immediately
  // If no cache, start as null (loading) - will show blur only if confirmed explorer
  // Validate cache against current auth token to ensure it's for the correct user
  const [isPaidUser, setIsPaidUser] = useState(() => {
    try {
      const authToken = localStorage.getItem('authToken')
      if (!authToken) {
        // No auth token, clear cache and return null
        try {
          localStorage.removeItem('custom_insights_isPaidUser')
          localStorage.removeItem('custom_insights_authTokenSnippet')
        } catch (e) { }
        return null
      }

      const cached = localStorage.getItem('custom_insights_isPaidUser')
      const cachedTokenSnippet = localStorage.getItem('custom_insights_authTokenSnippet')
      const currentTokenSnippet = authToken.slice(0, 8)

      // Only use cache if token snippet matches (same user)
      if (cached !== null && cachedTokenSnippet === currentTokenSnippet) {
        return cached === 'true'
      } else if (cached !== null) {
        // Token mismatch - clear old cache
        try {
          localStorage.removeItem('custom_insights_isPaidUser')
          localStorage.removeItem('custom_insights_authTokenSnippet')
        } catch (e) { }
      }
    } catch (e) { }
    // Default to null (loading) - blur will only show if confirmed as false (Explorer)
    return null
  })
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const topPerfFetchRequestId = React.useRef(0)
  const [windowWidth, setWindowWidth] = React.useState(typeof window !== "undefined" ? window.innerWidth : 1200)

  // Stable per-login/session identifier. Logout clears sessionStorage, so a new
  // login will get a new session id. We include this in React Query keys so
  // each login triggers a fresh call at least once, then rely on sessionStorage
  // to avoid repeated API calls during the same login session.
  const [dashboardSessionId] = React.useState(() => {
    try {
      const existing = sessionStorage.getItem("dashboard_session_id")
      if (existing) return existing
      const id = `${Date.now()}:${Math.random().toString(36).slice(2)}`
      sessionStorage.setItem("dashboard_session_id", id)
      return id
    } catch (_) {
      return "no-session-storage"
    }
  })

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Determine if user has a paid plan (Basic or Premium). Explorer (free) users see locked view.
  useEffect(() => {
    let cancelled = false
    const checkPlan = async () => {
      // Check if auth token exists - if not, clear cache and set as Explorer
      try {
        const authToken = localStorage.getItem('authToken')
        if (!authToken) {
          try {
            localStorage.removeItem('custom_insights_isPaidUser')
          } catch (e) { }
          if (!cancelled) setIsPaidUser(false)
          return
        }
      } catch (e) { }

      try {
        const result = await userProfileApi.fetchCurrentPlan()
        let paid = false
        if (result && result.data) {
          const planId = result.data.product
          const subscriptionId = result.data.subscription_id || result.data.id || null
          const subscriptionStatus = result.data.status || result.data.subscription_status || null
          const isSubscriptionActive =
            subscriptionId &&
            subscriptionStatus !== "cancelled" &&
            subscriptionStatus !== "canceled"
          paid = Boolean(planId && isSubscriptionActive)
        }
        if (!cancelled) {
          setIsPaidUser(paid)
          // Cache the result in localStorage to prevent flash on next load (persists across sessions)
          // Store with auth token snippet to validate on next load
          try {
            const authToken = localStorage.getItem('authToken')
            const tokenSnippet = authToken ? authToken.slice(0, 8) : ''
            localStorage.setItem('custom_insights_isPaidUser', String(paid))
            localStorage.setItem('custom_insights_authTokenSnippet', tokenSnippet)
          } catch (e) { }
        }
      } catch (e) {
        if (!cancelled) {
          setIsPaidUser(false)
          // Cache false (Explorer) on error in localStorage
          try {
            localStorage.setItem('custom_insights_isPaidUser', 'false')
          } catch (e) { }
        }
      }
    }
    // Always verify plan status, but cached value prevents initial flash
    checkPlan()
    return () => { cancelled = true }
  }, [])

  const isMobile = windowWidth < 768
  const cardGap = React.useMemo(() => {
    if (windowWidth < 768) return 12
    if (windowWidth < 1200) return 0.5
    return Math.min(1.5, 0.5 + (windowWidth - 1200) / 1000)
  }, [windowWidth])
  const cardGapHalf = cardGap / 2

  const [showTopPerfFilters, setShowTopPerfFilters] = useState(false)
  const [topPerfFilters, setTopPerfFilters] = useState(() => {
    try { const raw = sessionStorage.getItem('dashboard_topPerfFilters'); if (raw) return JSON.parse(raw); } catch (e) { }
    // Default to Meta Quest on first load (no saved filters yet)
    return { platform: DEFAULT_PLATFORM }
  })
  const [topPerfFiltersRaw, setTopPerfFiltersRaw] = useState(() => {
    try { const raw = sessionStorage.getItem('dashboard_topPerfFiltersRaw'); if (raw) return JSON.parse(raw); } catch (e) { }
    return { platform: DEFAULT_PLATFORM }
  })
  const [topPerfLastUpdated, setTopPerfLastUpdated] = useState(null)

  const [topPerfPage, setTopPerfPage] = useState(1)
  const topPerfPerPage = React.useMemo(() => (isMobile ? 5 : 10), [isMobile])

  const [selectedTopAppId, setSelectedTopAppId] = useState(null)
  const [selectedTopAppData, setSelectedTopAppData] = useState(null)
  const [selectedTopAppLoading, setSelectedTopAppLoading] = useState(false)

  // Most Tracked Apps (duplicate Top Performer behavior)
  const trackedFetchRequestId = React.useRef(0)
  const [showTrackedFilters, setShowTrackedFilters] = useState(false)
  const [trackedFilters, setTrackedFilters] = useState(() => {
    try { const raw = sessionStorage.getItem('custom_mostTrackedFilters'); if (raw) return JSON.parse(raw); } catch (e) { }
    // Default to Meta Quest on first load (no saved filters yet)
    return { platform: DEFAULT_PLATFORM }
  })
  const [trackedFiltersRaw, setTrackedFiltersRaw] = useState(() => {
    try { const raw = sessionStorage.getItem('custom_mostTrackedFiltersRaw'); if (raw) return JSON.parse(raw); } catch (e) { }
    return { platform: DEFAULT_PLATFORM }
  })
  const [trackedLastUpdated, setTrackedLastUpdated] = useState(null)

  const [trackedPage, setTrackedPage] = useState(1)
  const trackedPerPage = React.useMemo(() => (isMobile ? 5 : 10), [isMobile])

  const [selectedTrackedAppId, setSelectedTrackedAppId] = useState(null)
  const [selectedTrackedAppData, setSelectedTrackedAppData] = useState(null)
  const [selectedTrackedAppLoading, setSelectedTrackedAppLoading] = useState(false)

  // Remove a single tracked filter and recompute API filters (mirror OptimizedDashboard)
  const handleRemoveTrackedFilter = (key) => {
    const newFiltersRaw = { ...(trackedFiltersRaw || {}) }
    if (!Object.prototype.hasOwnProperty.call(newFiltersRaw, key)) return
    delete newFiltersRaw[key]
    setTrackedFiltersRaw(newFiltersRaw)
    try {
      sessionStorage.setItem('custom_mostTrackedFiltersRaw', JSON.stringify(newFiltersRaw || {}))
    } catch (e) { /* ignore */ }

    const apiFilters = {}
    if (newFiltersRaw) {
      const normalize = v => Array.isArray(v) ? v.join(",") : v
      if (newFiltersRaw.price) apiFilters.price = normalize(newFiltersRaw.price)
      if (newFiltersRaw.rating) apiFilters.rating = normalize(newFiltersRaw.rating)
      if (newFiltersRaw.revenue) apiFilters.revenue = normalize(newFiltersRaw.revenue)
      if (newFiltersRaw.release_date) apiFilters.release_date = normalize(newFiltersRaw.release_date)
      if (newFiltersRaw.genre) apiFilters.genre = normalize(newFiltersRaw.genre)
      if (newFiltersRaw.platform) apiFilters.platform = normalize(newFiltersRaw.platform)
      if (newFiltersRaw.bundle) apiFilters.bundle = normalize(newFiltersRaw.bundle)
      if (newFiltersRaw.number_of_players) apiFilters.number_of_players = normalize(newFiltersRaw.number_of_players)
      if (newFiltersRaw.sort_by || newFiltersRaw.sort) apiFilters.sort_by = newFiltersRaw.sort_by || newFiltersRaw.sort
    }
    setTrackedFilters(apiFilters)
    try {
      sessionStorage.setItem('custom_mostTrackedFilters', JSON.stringify(apiFilters || {}))
    } catch (e) { /* ignore */ }
    setTrackedPage(1)
  }

  // Single-select platform pill row next to the "Top Tracked Apps" title — picking
  // one platform replaces whatever was selected before (only one active at a time);
  // clicking the already-active platform clears the filter back to "all".
  const handleTrackedPlatformToggle = (platformId) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PLATFORM_CHANGED, { section: "tracked", platform: platformId })
    const current = Array.isArray(trackedFiltersRaw?.platform) ? trackedFiltersRaw.platform : []
    const isAlreadyActive = current.length === 1 && current[0] === platformId
    const next = isAlreadyActive ? [] : [platformId]
    const newFiltersRaw = { ...(trackedFiltersRaw || {}) }
    if (next.length) newFiltersRaw.platform = next
    else delete newFiltersRaw.platform
    setTrackedFiltersRaw(newFiltersRaw)
    try {
      sessionStorage.setItem('custom_mostTrackedFiltersRaw', JSON.stringify(newFiltersRaw || {}))
    } catch (e) { /* ignore */ }

    const apiFilters = {}
    const normalize = v => Array.isArray(v) ? v.join(",") : v
    if (newFiltersRaw.price) apiFilters.price = normalize(newFiltersRaw.price)
    if (newFiltersRaw.rating) apiFilters.rating = normalize(newFiltersRaw.rating)
    if (newFiltersRaw.revenue) apiFilters.revenue = normalize(newFiltersRaw.revenue)
    if (newFiltersRaw.release_date) apiFilters.release_date = normalize(newFiltersRaw.release_date)
    if (newFiltersRaw.genre) apiFilters.genre = normalize(newFiltersRaw.genre)
    if (newFiltersRaw.platform) apiFilters.platform = normalize(newFiltersRaw.platform)
    if (newFiltersRaw.bundle) apiFilters.bundle = normalize(newFiltersRaw.bundle)
    if (newFiltersRaw.number_of_players) apiFilters.number_of_players = normalize(newFiltersRaw.number_of_players)
    if (newFiltersRaw.sort_by || newFiltersRaw.sort) apiFilters.sort_by = newFiltersRaw.sort_by || newFiltersRaw.sort
    setTrackedFilters(apiFilters)
    try {
      sessionStorage.setItem('custom_mostTrackedFilters', JSON.stringify(apiFilters || {}))
    } catch (e) { /* ignore */ }
    setTrackedPage(1)
  }

  // Remove a single Top Performer filter and recompute API filters (mirror OptimizedDashboard)
  const handleRemoveTopPerfFilter = (key) => {
    const newFiltersRaw = { ...(topPerfFiltersRaw || {}) }
    if (!Object.prototype.hasOwnProperty.call(newFiltersRaw, key)) return
    delete newFiltersRaw[key]
    setTopPerfFiltersRaw(newFiltersRaw)
    try {
      sessionStorage.setItem('dashboard_topPerfFiltersRaw', JSON.stringify(newFiltersRaw || {}))
    } catch (e) { /* ignore */ }

    const apiFilters = {}
    if (newFiltersRaw) {
      const normalize = v => Array.isArray(v) ? v.join(",") : v
      if (newFiltersRaw.price) apiFilters.price = normalize(newFiltersRaw.price)
      if (newFiltersRaw.rating) apiFilters.rating = normalize(newFiltersRaw.rating)
      if (newFiltersRaw.revenue) apiFilters.revenue = normalize(newFiltersRaw.revenue)
      if (newFiltersRaw.release_date) apiFilters.release_date = normalize(newFiltersRaw.release_date)
      if (newFiltersRaw.genre) apiFilters.genre = normalize(newFiltersRaw.genre)
      if (newFiltersRaw.platform) apiFilters.platform = normalize(newFiltersRaw.platform)
      if (newFiltersRaw.bundle) apiFilters.bundle = normalize(newFiltersRaw.bundle)
      if (newFiltersRaw.number_of_players) apiFilters.number_of_players = normalize(newFiltersRaw.number_of_players)
      if (newFiltersRaw.sort_by || newFiltersRaw.sort) {
        const rawSort = newFiltersRaw.sort_by || newFiltersRaw.sort
        const s = Array.isArray(rawSort) ? rawSort[0] : rawSort
        apiFilters.sort_by = String(s).replace(/\?/g, "")
      }
    }
    setTopPerfFilters(apiFilters)
    try {
      sessionStorage.setItem('dashboard_topPerfFilters', JSON.stringify(apiFilters || {}))
    } catch (e) { /* ignore */ }
    setTopPerfPage(1)
  }

  // Single-select platform pill row next to the "Top Performer Apps" title — picking
  // one platform replaces whatever was selected before (only one active at a time);
  // clicking the already-active platform clears the filter back to "all".
  const handleTopPerfPlatformToggle = (platformId) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PLATFORM_CHANGED, { section: "top_performer", platform: platformId })
    const current = Array.isArray(topPerfFiltersRaw?.platform) ? topPerfFiltersRaw.platform : []
    const isAlreadyActive = current.length === 1 && current[0] === platformId
    const next = isAlreadyActive ? [] : [platformId]
    const newFiltersRaw = { ...(topPerfFiltersRaw || {}) }
    if (next.length) newFiltersRaw.platform = next
    else delete newFiltersRaw.platform
    setTopPerfFiltersRaw(newFiltersRaw)
    try {
      sessionStorage.setItem('dashboard_topPerfFiltersRaw', JSON.stringify(newFiltersRaw || {}))
    } catch (e) { /* ignore */ }

    const apiFilters = {}
    const normalize = v => Array.isArray(v) ? v.join(",") : v
    if (newFiltersRaw.price) apiFilters.price = normalize(newFiltersRaw.price)
    if (newFiltersRaw.rating) apiFilters.rating = normalize(newFiltersRaw.rating)
    if (newFiltersRaw.revenue) apiFilters.revenue = normalize(newFiltersRaw.revenue)
    if (newFiltersRaw.release_date) apiFilters.release_date = normalize(newFiltersRaw.release_date)
    if (newFiltersRaw.genre) apiFilters.genre = normalize(newFiltersRaw.genre)
    if (newFiltersRaw.platform) apiFilters.platform = normalize(newFiltersRaw.platform)
    if (newFiltersRaw.bundle) apiFilters.bundle = normalize(newFiltersRaw.bundle)
    if (newFiltersRaw.number_of_players) apiFilters.number_of_players = normalize(newFiltersRaw.number_of_players)
    if (newFiltersRaw.sort_by || newFiltersRaw.sort) {
      const rawSort = newFiltersRaw.sort_by || newFiltersRaw.sort
      const s = Array.isArray(rawSort) ? rawSort[0] : rawSort
      apiFilters.sort_by = String(s).replace(/\?/g, "")
    }
    setTopPerfFilters(apiFilters)
    try {
      sessionStorage.setItem('dashboard_topPerfFilters', JSON.stringify(apiFilters || {}))
    } catch (e) { /* ignore */ }
    setTopPerfPage(1)
  }

  const {
    data: trackedAppsData = [],
    isLoading: trackedLoading,
  } = useQuery({
    // Include filters and a per-login session id in the query key so changes
    // re-fetch and each login triggers a fresh call at least once.
    queryKey: [
      ...queryKeys.dashboard.trackedApps(),
      dashboardSessionId,
      JSON.stringify(trackedFilters),
    ],
    enabled: isPaidUser === true,
    queryFn: async () => {
      return measureOperation("fetchTrackedApps", async () => {
        const storageKey =
          "trackedApps" + JSON.stringify(trackedFilters || {})
        const sessionAuthKey = "dashboard_auth_token"

        // Read current auth token and the token associated with the cached data
        let authToken = null
        let cachedAuthToken = null
        try {
          authToken = localStorage.getItem("authToken") || null
        } catch (_) { }
        try {
          cachedAuthToken = sessionStorage.getItem(sessionAuthKey) || null
        } catch (_) { }

        // If this browser session already has cached data for this filter set
        // AND it was generated under the same auth token, return it without
        // hitting the backend again.
        if (cachedAuthToken && authToken && cachedAuthToken === authToken) {
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              if (arr.length > 0) {
                // Also ensure updated_at from cache is available for useEffect
                const updatedAtKey = storageKey + "_updated_at"
                const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
                if (cachedUpdatedAt) {
                  // Keep it in sessionStorage for the useEffect to pick up
                  sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
                }
                return arr
              }
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to fresh fetch
          }
        }

        // Either first login for this session, auth token changed (logout/login),
        // or there is no cached data for this filter set: fetch fresh data and
        // bind it to the current auth token in sessionStorage.
        try {
          const response = await apiService.fetchTrackedApps(trackedFilters)
          const games = response?.data || response || []
          const updatedAt = response?.updated_at || null

          sessionStorage.setItem(storageKey, JSON.stringify(games))
          // Store updated_at timestamp separately for display
          if (updatedAt) {
            const updatedAtKey = storageKey + "_updated_at"
            sessionStorage.setItem(updatedAtKey, updatedAt)
          }
          if (authToken) {
            sessionStorage.setItem(sessionAuthKey, authToken)
          } else {
            sessionStorage.removeItem(sessionAuthKey)
          }
          return games
        } catch (error) {
          console.error("Error fetching tracked apps:", error)
          // On error, fall back to any previously cached data in sessionStorage
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              // Also ensure updated_at from cache is available
              const updatedAtKey = storageKey + "_updated_at"
              const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
              if (cachedUpdatedAt) {
                sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
              }
              return arr
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to empty array
          }
          return []
        }
      })
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const _rawTracked = Array.isArray(trackedAppsData) ? trackedAppsData : []
  const seenTrackedIds = new Set()
  const fullTracked = _rawTracked.filter(app => {
    if (!app || !app.item_id) return false
    if (seenTrackedIds.has(app.item_id)) return false
    seenTrackedIds.add(app.item_id)
    return true
  })

  const trackedTotalPages = Math.max(1, Math.ceil(fullTracked.length / trackedPerPage))
  const trackedPageSafe = Math.min(Math.max(1, trackedPage), trackedTotalPages)
  const trackedCurrent = fullTracked.slice((trackedPageSafe - 1) * trackedPerPage, trackedPageSafe * trackedPerPage)

  const handleTrackedClick = async (app, { forceRefresh = false } = {}) => {
    if (!app || !app.item_id) return
    if (selectedTrackedAppId === app.item_id && !forceRefresh) {
      setSelectedTrackedAppId(null)
      setSelectedTrackedAppData(null)
      return
    }
    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_APP_SELECTED, { app_id: app.item_id, app_name: app.name })

    setSelectedTrackedAppId(app.item_id)
    setSelectedTrackedAppLoading(true)
    trackedFetchRequestId.current += 1
    const reqId = trackedFetchRequestId.current
    try {
      const res = await apiService.fetchAppGraph(app.item_id)
      const payload = res?.data || res || []
      let normalized = []
      if (Array.isArray(payload)) normalized = payload
      else if (payload?.history?.historic_entries) normalized = payload.history.historic_entries
      else if (payload?.historic_entries) normalized = payload.historic_entries
      else normalized = payload

      const mapped = (Array.isArray(normalized) ? normalized : []).map(entry => {
        const epoch = entry.created_at || entry.created_at_date || entry.ts || null
        const dateObj = epoch ? new Date(Number(epoch) * 1000) : null
        const created_at_date = dateObj ? dateObj.toISOString().slice(0, 10) : entry.created_at_date || null
        return { ...entry, created_at_date }
      })

      if (reqId === trackedFetchRequestId.current) setSelectedTrackedAppData(mapped)
      else console.warn("Ignored stale app_graph response", app.item_id)
    } catch (err) {
      console.error("Error loading app graph:", err)
      if (reqId === trackedFetchRequestId.current) setSelectedTrackedAppData(null)
    } finally {
      if (reqId === trackedFetchRequestId.current) setSelectedTrackedAppLoading(false)
    }
  }

  const handleTrackAndRedirect = async (app) => {
    if (!app || !app.item_id) return
    try {
      const payload = { item_id: app.item_id }
      await axios.post(`${api}/tracker/tracking/add_category_tracking`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      })
    } catch (err) {
      console.error('Error adding app to tracking:', err)
    } finally {
      // Land directly on this app's App Store Listing page (same route/state the App Tracking list uses)
      const listingUrl = `/xr/app/${generateSlug(app.name || '')}?id=${app.item_id}`
      try {
        navigate(listingUrl, { state: { selected_tab: 0, data: app, source_surface: "custom_insights_top_tracked" } })
      } catch (e) {
        window.location.href = listingUrl
      }
    }
  }

  React.useEffect(() => {
    try {
      if (!trackedLoading && Array.isArray(trackedCurrent) && trackedCurrent.length > 0) {
        handleTrackedClick(trackedCurrent[0], { forceRefresh: true })
      } else {
        setSelectedTrackedAppId(null)
        setSelectedTrackedAppData(null)
      }
    } catch (e) {
      console.warn("Auto-opening tracked app after page change failed", e)
    }
  }, [trackedPageSafe, trackedLoading, trackedAppsData])

  React.useEffect(() => {
    try {
      if (!trackedLoading) {
        const hasArrayData = Array.isArray(trackedAppsData) && trackedAppsData.length > 0
        const hasObjectData = trackedAppsData && typeof trackedAppsData === "object" && !Array.isArray(trackedAppsData) && Object.keys(trackedAppsData).length > 0
        if (hasArrayData || hasObjectData) {
          // Try to get updated_at from sessionStorage (stored by queryFn)
          const storageKey = "trackedApps" + JSON.stringify(trackedFilters || {})
          const updatedAtKey = storageKey + "_updated_at"
          const storedUpdatedAt = sessionStorage.getItem(updatedAtKey)

          if (storedUpdatedAt) {
            // Parse the ISO timestamp string and convert to timestamp
            const parsedDate = new Date(storedUpdatedAt)
            if (!isNaN(parsedDate.getTime())) {
              setTrackedLastUpdated(parsedDate.getTime())
              return
            }
          }
          // Fallback to current time if no updated_at available
          setTrackedLastUpdated(Date.now())
        }
      }
    } catch (e) { console.warn("Error setting tracked last updated", e) }
  }, [trackedLoading, trackedAppsData, trackedFilters])

  const { data: topPerformanceApps = [], isLoading: topPerfLoading } = useQuery({
    queryKey: [
      ...queryKeys.dashboard.topPerformance(),
      dashboardSessionId,
      JSON.stringify(topPerfFilters),
    ],
    enabled: isPaidUser === true,
    queryFn: async () => {
      return measureOperation("fetchTopPerformance", async () => {
        const storageKey =
          "topPerformance" + JSON.stringify(topPerfFilters || {})
        const sessionAuthKey = "dashboard_auth_token"

        // Read current auth token and the token associated with the cached data
        let authToken = null
        let cachedAuthToken = null
        try {
          authToken = localStorage.getItem("authToken") || null
        } catch (_) { }
        try {
          cachedAuthToken = sessionStorage.getItem(sessionAuthKey) || null
        } catch (_) { }

        // If this browser session already has cached data for this filter set
        // AND it was generated under the same auth token, return it without
        // hitting the backend again.
        if (cachedAuthToken && authToken && cachedAuthToken === authToken) {
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              if (arr.length > 0) {
                // Also ensure updated_at from cache is available for useEffect
                const updatedAtKey = storageKey + "_updated_at"
                const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
                if (cachedUpdatedAt) {
                  // Keep it in sessionStorage for the useEffect to pick up
                  sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
                }
                // "Top Performer Apps" is ranked by KeyQuest's App Score, so this
                // maps to the TOP_APP_SCORES insight type.
                trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_VIEWED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_APP_SCORES })
                return arr
              }
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to fresh fetch
          }
        }

        // Either first login for this session, auth token changed (logout/login),
        // or there is no cached data for this filter set: fetch fresh data and
        // bind it to the current auth token in sessionStorage.
        try {
          const response = await apiService.fetchTopPerformance(topPerfFilters)
          const games = response?.data || response || []
          const updatedAt = response?.updated_at || null

          sessionStorage.setItem(storageKey, JSON.stringify(games))
          // Store updated_at timestamp separately for display
          if (updatedAt) {
            const updatedAtKey = storageKey + "_updated_at"
            sessionStorage.setItem(updatedAtKey, updatedAt)
          }
          if (authToken) {
            sessionStorage.setItem(sessionAuthKey, authToken)
          } else {
            sessionStorage.removeItem(sessionAuthKey)
          }
          trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_VIEWED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_APP_SCORES })
          return games
        } catch (error) {
          console.error("Error fetching top performance apps:", error)
          // On error, fall back to any previously cached data in sessionStorage
          try {
            const storedData = sessionStorage.getItem(storageKey)
            if (storedData) {
              const parsed = JSON.parse(storedData)
              const arr = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.data)
                  ? parsed.data
                  : []
              // Also ensure updated_at from cache is available
              const updatedAtKey = storageKey + "_updated_at"
              const cachedUpdatedAt = sessionStorage.getItem(updatedAtKey)
              if (cachedUpdatedAt) {
                sessionStorage.setItem(updatedAtKey, cachedUpdatedAt)
              }
              return arr
            }
          } catch (_) {
            // ignore parse/storage errors and fall through to empty array
          }
          return []
        }
      })
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 15,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const _rawTop = Array.isArray(topPerformanceApps) ? topPerformanceApps : []
  const seenTopIds = new Set()
  const fullTop = _rawTop.filter(app => {
    if (!app || !app.item_id) return false
    if (seenTopIds.has(app.item_id)) return false
    seenTopIds.add(app.item_id)
    return true
  })

  const topTotalPages = Math.max(1, Math.ceil(fullTop.length / topPerfPerPage))
  const topPageSafe = Math.min(Math.max(1, topPerfPage), topTotalPages)
  const topCurrent = fullTop.slice((topPageSafe - 1) * topPerfPerPage, topPageSafe * topPerfPerPage)

  const handleTopPerfClick = async (app, { forceRefresh = false } = {}) => {
    if (!app || !app.item_id) return
    if (selectedTopAppId === app.item_id && !forceRefresh) {
      setSelectedTopAppId(null)
      setSelectedTopAppData(null)
      return
    }

    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_APP_SELECTED, { app_id: app.item_id, app_name: app.name })

    setSelectedTopAppId(app.item_id)
    setSelectedTopAppLoading(true)
    topPerfFetchRequestId.current += 1
    const reqId = topPerfFetchRequestId.current
    try {
      const res = await apiService.fetchAppGraph(app.item_id)
      const payload = res?.data || res || []
      let normalized = []
      if (Array.isArray(payload)) normalized = payload
      else if (payload?.history?.historic_entries) normalized = payload.history.historic_entries
      else if (payload?.historic_entries) normalized = payload.historic_entries
      else normalized = payload

      const mapped = (Array.isArray(normalized) ? normalized : []).map(entry => {
        const epoch = entry.created_at || entry.created_at_date || entry.ts || null
        const dateObj = epoch ? new Date(Number(epoch) * 1000) : null
        const created_at_date = dateObj ? dateObj.toISOString().slice(0, 10) : entry.created_at_date || null
        return { ...entry, created_at_date }
      })

      if (reqId === topPerfFetchRequestId.current) setSelectedTopAppData(mapped)
      else console.warn("Ignored stale app_graph response", app.item_id)
    } catch (err) {
      console.error("Error loading app graph:", err)
      if (reqId === topPerfFetchRequestId.current) setSelectedTopAppData(null)
    } finally {
      if (reqId === topPerfFetchRequestId.current) setSelectedTopAppLoading(false)
    }
  }

  React.useEffect(() => {
    try {
      if (!topPerfLoading && Array.isArray(topCurrent) && topCurrent.length > 0) {
        handleTopPerfClick(topCurrent[0], { forceRefresh: true })
      } else {
        setSelectedTopAppId(null)
        setSelectedTopAppData(null)
      }
    } catch (e) {
      console.warn("Auto-opening top performer after page change failed", e)
    }
  }, [topPageSafe, topPerfLoading, topPerformanceApps])

  React.useEffect(() => {
    try {
      if (!topPerfLoading) {
        const hasArrayData = Array.isArray(topPerformanceApps) && topPerformanceApps.length > 0
        const hasObjectData = topPerformanceApps && typeof topPerformanceApps === "object" && !Array.isArray(topPerformanceApps) && Object.keys(topPerformanceApps).length > 0
        if (hasArrayData || hasObjectData) {
          // Try to get updated_at from sessionStorage (stored by queryFn)
          const storageKey = "topPerformance" + JSON.stringify(topPerfFilters || {})
          const updatedAtKey = storageKey + "_updated_at"
          const storedUpdatedAt = sessionStorage.getItem(updatedAtKey)

          if (storedUpdatedAt) {
            // Parse the ISO timestamp string and convert to timestamp
            const parsedDate = new Date(storedUpdatedAt)
            if (!isNaN(parsedDate.getTime())) {
              setTopPerfLastUpdated(parsedDate.getTime())
              return
            }
          }
          // Fallback to current time if no updated_at available
          setTopPerfLastUpdated(Date.now())
        }
      }
    } catch (e) { console.warn("Error setting top performance last updated", e) }
  }, [topPerfLoading, topPerformanceApps, topPerfFilters])

  React.useEffect(() => {
    try {
      const key = "dashboard_topPerfPage_" + JSON.stringify(topPerfFilters || {})
      const stored = sessionStorage.getItem(key)
      if (stored) {
        const parsed = parseInt(stored, 10) || 1
        const clamped = Math.min(Math.max(1, parsed), topTotalPages)
        setTopPerfPage(clamped)
      }
    } catch (e) { }
  }, [topPerfFilters, topTotalPages])

  React.useEffect(() => {
    try { const key = "dashboard_topPerfPage_" + JSON.stringify(topPerfFilters || {}); sessionStorage.setItem(key, String(topPerfPage)) } catch (e) { }
  }, [topPerfPage, topPerfFilters])

  return (
    <React.Fragment>
      <SEO title={applySEODate(PAGE_SEO.customInsights.title)} description={PAGE_SEO.customInsights.description} keywords={PAGE_SEO.customInsights.keywords} canonical={generateCanonicalUrl("/custom-insights")} robots={PAGE_SEO.customInsights.robots} structuredData={[STRUCTURED_DATA.organization, STRUCTURED_DATA.website]} />

      {/* Styles moved to src/pages/CustomInsight/CustomInsightsDashboard.css */}

      <div className="page-content dashboard-responsive custom-insights-dashboard">
        <Container fluid>
          <Row className="mb-4">
            <Col>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h1 className="mb-2" style={{
                    fontFamily: 'Rubik',
                    fontWeight: 700,
                    fontSize: '32px',
                    color: "rgba(255, 255, 255, 1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}>
                    Custom KeyQuest Insights
                  </h1>
                  <p className="mb-0" style={{
                    fontSize: '16px',
                    color: '#E8E8E8',
                    opacity: 0.8,
                    maxWidth: '800px',
                    lineHeight: '1.5',
                    marginTop: 4
                  }}>
                    Track all your data with custom KeyQuest analysis across your tracked apps, games, and keywords. Gain deeper visibility into performance, growth trends, and revenue-driving factors using advanced analytics and AI-powered insights.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
          {isPaidUser === false && (
            <div className="mt-2 mb-4">
              <h2 style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: '20px', color: '#fff', marginBottom: 8 }}>
                Explore Free Insights
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', marginBottom: 20 }}>
                These insights are available on your current plan — no upgrade needed.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {[
                  { to: '/top-genres-2025', label: 'Top Genres of 2025', icon: 'fas fa-tags' },
                  { to: '/top-revenued', label: 'Top Revenued', icon: 'fas fa-dollar-sign' },
                  { to: '/top-downloaded', label: 'Top Downloads', icon: 'fas fa-download' },
                  { to: '/top-rated', label: 'Top Average Rating', icon: 'fas fa-star' },
                  { to: '/all-time-hits', label: 'Top App Score', icon: 'fas fa-trophy' },
                ].map(({ to, label, icon }) => (
                  <Link key={to} to={to} style={{ textDecoration: 'none' }}>
                    <div
                      style={{ background: '#21222D', border: '1px solid #353748', borderRadius: 10, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#F6255A'; e.currentTarget.style.background = '#2a1a20' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#353748'; e.currentTarget.style.background = '#21222D' }}
                    >
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(246,37,90,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={icon} style={{ color: '#F6255A', fontSize: 15 }} />
                      </div>
                      <span style={{ color: '#fff', fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>{label}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="premium-section-wrapper">
            {isPaidUser === false && (
              <div className="premium-lock-overlay">
                <button
                  type="button"
                  className="premium-lock-icon-button"
                  onClick={() => setShowUpgradeModal(true)}
                  aria-label="Unlock with Premium"
                  title="Unlock with Premium"
                >
                  <span className="premium-lock-icon-circle" aria-hidden="true">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 17a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
                      <path d="M17 8V7a5 5 0 10-10 0v1H5v11h14V8h-2zm-8-1a4 4 0 118 0v1H9V7z" fill="currentColor" />
                    </svg>
                  </span>
                </button>
              </div>
            )}
            <div
              className={
                // Only show blur when confirmed Explorer (false), not during loading (null)
                // This prevents premium users from seeing blur flash
                isPaidUser === false
                  ? "premium-blur-content"
                  : ""
              }
              onClick={
                // Only make clickable when confirmed Explorer (false)
                isPaidUser === false
                  ? () => setShowUpgradeModal(true)
                  : undefined
              }
            >
              <div className="mt-4 w-100" style={{ background: "#21222D", border: "1px solid #353748", borderRadius: "8px", overflow: "visible" }}>
                <div className="rounded-3 p-3 p-lg-4" style={{ backgroundColor: "#21222D", overflow: "visible" }}>
                  {/* Most Tracked Games - updated header & description */}
                  <h2 className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ visibility: "visible", display: "flex", position: "relative", zIndex: 1 }}>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 14 }}>
                      <span style={{ fontFamily: "Rubik", fontWeight: 600, fontSize: "clamp(16px, 2vw, 22px)", color: "rgba(255, 255, 255, 1)", visibility: "visible", display: "block" }}>
                        Top Tracked Apps
                      </span>
                      {/* Platform pills next to the title — desktop only; on smaller
                          screens they drop to their own row below (see d-lg-none block). */}
                      <div className="d-none d-lg-flex">
                        <PlatformTabs
                          variant="pill"
                          showDivider={false}
                          selected={Array.isArray(trackedFiltersRaw?.platform) ? trackedFiltersRaw.platform : []}
                          onToggle={handleTrackedPlatformToggle}
                        />
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button type="button" className="filter-trigger" onClick={() => setShowTrackedFilters(true)} aria-expanded={showTrackedFilters} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 10, padding: "7px 10px", display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", fontWeight: 600, fontSize: "14px", lineHeight: 1 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginRight: 7 }} xmlns="http://www.w3.org/2000/svg"><path d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z" fill="#fff" /></svg>
                          Filter
                          {/* `platform` is excluded here — it's the pill row below (outside the
                              Filters modal), not a modal filter, so it shouldn't bump this badge. */}
                          {trackedFiltersRaw && Object.keys(trackedFiltersRaw).filter(k => k !== "platform").length > 0 && (
                            <span className="filter-badge">{Object.entries(trackedFiltersRaw).reduce((acc, [key, v]) => { if (key === "platform") return acc; if (v == null) return acc; if (Array.isArray(v)) return acc + v.length; if (typeof v === "string" || typeof v === "number") return acc + (String(v).trim() ? 1 : 0); return acc + 1 }, 0)}</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </h2>

                  {/* Same platform pills, shown on their own row below the title on
                      mobile/tablet where there isn't room for them next to it. */}
                  <div className="d-lg-none" style={{ marginTop: "-16px", marginBottom: "10px" }}>
                    <PlatformTabs
                      variant="pill"
                      showDivider={false}
                      selected={Array.isArray(trackedFiltersRaw?.platform) ? trackedFiltersRaw.platform : []}
                      onToggle={handleTrackedPlatformToggle}
                    />
                  </div>

                  <p style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "14px", lineHeight: 1.4, marginTop: "-6px", marginBottom: "14px" }}>
                    A focused view of your most impactful apps based on real performance metrics.
                  </p>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", whiteSpace: "nowrap" }}>
                      Last updated: {trackedLastUpdated ? new Date(trackedLastUpdated).toLocaleString() : "—"}
                    </div>
                  </div>
                  {Object.keys(trackedFiltersRaw || {}).filter(k => k !== "platform").length > 0 && (
                    <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {Object.entries(trackedFiltersRaw).map(([key, value]) => {
                        if (key === "platform") return null
                        if (!value || (Array.isArray(value) && value.length === 0)) return null
                        const displayValue = Array.isArray(value) ? value.join(', ') : value
                        return (
                          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                            <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                            <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayValue}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                handleRemoveTrackedFilter(key)
                              }}
                              aria-label={`Remove filter ${key}`}
                              style={{
                                marginLeft: 4,
                                background: 'transparent',
                                border: 'none',
                                color: '#F6255A',
                                cursor: 'pointer',
                                fontSize: 12,
                                lineHeight: 0.9,
                              }}
                            >
                              ×
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                  {showTrackedFilters && (
                    <FilterPanel open={showTrackedFilters} hidePlatformSection sections={filterSections.length > 0 ? filterSections : undefined} initialValues={trackedFiltersRaw} onClose={() => setShowTrackedFilters(false)} onApply={sel => {
                      const apiFilters = {}
                      if (sel) {
                        const normalize = v => Array.isArray(v) ? v.join(",") : v
                        if (sel.price) apiFilters.price = normalize(sel.price)
                        if (sel.rating) apiFilters.rating = normalize(sel.rating)
                        if (sel.revenue) apiFilters.revenue = normalize(sel.revenue)
                        if (sel.release_date) apiFilters.release_date = normalize(sel.release_date)
                        if (sel.genre) apiFilters.genre = normalize(sel.genre)
                        if (sel.platform) apiFilters.platform = normalize(sel.platform)
                        if (sel.bundle) apiFilters.bundle = normalize(sel.bundle)
                        if (sel.number_of_players) apiFilters.number_of_players = normalize(sel.number_of_players)
                        if (sel.sort_by || sel.sort) apiFilters.sort_by = sel.sort_by || sel.sort
                      }
                      Object.keys(sessionStorage).forEach(key => { if (key && key.startsWith("trackedApps")) sessionStorage.removeItem(key) })
                      setTrackedFilters(apiFilters)
                      setTrackedFiltersRaw(sel || {})
                      try { sessionStorage.setItem('custom_mostTrackedFilters', JSON.stringify(apiFilters || {})); sessionStorage.setItem('custom_mostTrackedFiltersRaw', JSON.stringify(sel || {})); } catch (e) { }
                      setTrackedPage(1)
                      setShowTrackedFilters(false)
                    }} />
                  )}
                  <Row className="trending_slider">
                    {trackedLoading ? (
                      <div style={{ paddingLeft: isMobile ? "0" : `${cardGap}px`, paddingRight: isMobile ? "0" : `${cardGapHalf}px` }}>
                        <div className="d-flex gap-3" style={{ gap: isMobile ? "12px" : `${cardGap}px` }}>
                          {Array.from({ length: trackedPerPage }).map((_, idx) => {
                            const cardDims = isMobile ? { maxWidth: "220px", minWidth: "190px", height: "225px" } : { maxWidth: "160px", minWidth: "140px", height: "130px" }
                            return (
                              <div key={idx} style={{ minWidth: cardDims.minWidth, maxWidth: cardDims.maxWidth }}>
                                <Skeleton height={cardDims.height} borderRadius={8} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />
                                {isMobile && <Skeleton width="70%" height={16} style={{ marginTop: "12px" }} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ) : fullTracked && fullTracked.length > 0 ? (
                      <div style={{ paddingLeft: isMobile ? "0" : `${cardGap}px`, paddingRight: isMobile ? "0" : `${cardGapHalf}px` }}>
                        <CarouselComponent games={trackedCurrent} defaultOpenGraph={"app_score"} disableLink={true} onCardClick={handleTrackedClick} selectedAppId={selectedTrackedAppId} cardDimsOverride={{ mobile: { maxWidth: '220px', minWidth: '190px', aspectRatio: '4 / 3', padding: '10px' } }} slidesToShowOverride={isMobile ? 1 : undefined} showTrackButton={true} onTrack={handleTrackAndRedirect} fallbackPlatformId={Array.isArray(trackedFiltersRaw?.platform) && trackedFiltersRaw.platform.length === 1 ? trackedFiltersRaw.platform[0] : null} />

                        {!trackedLoading && trackedTotalPages > 1 && (
                          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                            <button type="button" onClick={() => setTrackedPage(p => { const next = Math.max(1, p - 1); trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PAGINATED, { section: "tracked", page: next }); return next })} disabled={trackedPageSafe <= 1} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.06)", color: trackedPageSafe <= 1 ? "rgba(255,255,255,0.35)" : "#fff", padding: "4px 8px", borderRadius: 4, fontSize: "12px", cursor: trackedPageSafe <= 1 ? "not-allowed" : "pointer" }}>Prev</button>

                            {getPageNumbers(trackedPageSafe, trackedTotalPages).map((page, index) => {
                              if (page === "...") return (<span key={`dots-tr-${index}`} style={{ color: "rgba(255,255,255,0.6)", padding: "6px 8px" }}>...</span>)
                              return (
                                <button key={`tr-${page}`} type="button" onClick={() => { const next = Math.min(Math.max(1, page), trackedTotalPages); trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PAGINATED, { section: "tracked", page: next }); setTrackedPage(next) }} style={{ minWidth: 28, height: 28, borderRadius: 4, border: "1px solid rgba(255,255,255,0.06)", background: trackedPageSafe === page ? "#ff2f6d" : "transparent", color: trackedPageSafe === page ? "#fff" : "rgba(255,255,255,0.8)", fontWeight: 600, fontSize: "12px", cursor: "pointer" }}>{page}</button>
                              )
                            })}

                            <button type="button" onClick={() => setTrackedPage(p => { const next = Math.min(trackedTotalPages, p + 1); trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PAGINATED, { section: "tracked", page: next }); return next })} disabled={trackedPageSafe >= trackedTotalPages} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.06)", color: trackedPageSafe >= trackedTotalPages ? "rgba(255,255,255,0.35)" : "#fff", padding: "4px 8px", borderRadius: 4, fontSize: "12px", cursor: trackedPageSafe >= trackedTotalPages ? "not-allowed" : "pointer" }}>Next</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding: 18, color: "rgba(255,255,255,0.8)" }}>No apps available</div>
                    )}
                  </Row>
                  {selectedTrackedAppId && (() => {
                    const selectedApp = fullTracked?.find(a => a.item_id === selectedTrackedAppId) || null
                    return (
                      <div style={{ marginTop: 18, padding: 12, borderRadius: 8, background: "#18181f", border: "1px solid #2f2f3a" }}>
                        <div className="d-flex gap-3 mb-3" style={{ flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: isMobile ? "flex-start" : "space-between", gap: isMobile ? 12 : undefined }}>
                          <div style={{ display: "flex", gap: 12, alignItems: "center", width: isMobile ? "100%" : "auto", justifyContent: isMobile ? "space-between" : "flex-start", flex: isMobile ? "none" : 1 }}>
                            <div style={{ width: isMobile ? 56 : 72, height: isMobile ? 56 : 72, borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
                              <img src={selectedApp?.image_url || ""} alt="app" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            </div>

                            {!isMobile && (
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {(() => {
                                  const daysCount = Array.isArray(selectedTrackedAppData) ? selectedTrackedAppData.length : 0
                                  const label = daysCount >= 7 ? "7 days data" : `${daysCount} day${daysCount === 1 ? "" : "s"}`
                                  return (
                                    <div style={{ marginBottom: 6 }}>
                                      <span style={{ fontSize: 12, background: "#2b2b35", color: "rgba(255,255,255,0.85)", padding: "4px 8px", borderRadius: 999, fontWeight: 600 }}>{label}</span>
                                    </div>
                                  )
                                })()}

                                <h3 style={{ margin: 0, color: "#fff", fontSize: '18px', fontWeight: 500 }}>{selectedApp?.name || "App"}</h3>
                                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>App Score trend</div>
                              </div>
                            )}

                            {isMobile && (
                              <div style={{ flexShrink: 0, alignSelf: "flex-start" }}>
                                <Link to={`/xr/app/${generateSlug(selectedApp?.name || "")}?id=${selectedTrackedAppId}`} state={{ app_id: selectedTrackedAppId, data: selectedApp, openGraph: "app_score" }} style={{ textDecoration: "none" }}>
                                  <button type="button" style={{ background: "transparent", border: "1px solid #ff4f7a", color: "#fff", borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontWeight: 500, fontSize: "11px", whiteSpace: "nowrap" }}>View App Details</button>
                                </Link>
                              </div>
                            )}
                          </div>

                          {!isMobile && (
                            <div style={{ flexShrink: 0, alignSelf: "center" }}>
                              <Link to={`/xr/app/${generateSlug(selectedApp?.name || "")}?id=${selectedTrackedAppId}`} state={{ app_id: selectedTrackedAppId, data: selectedApp, openGraph: "app_score" }} style={{ textDecoration: "none" }}>
                                <button type="button" style={{ background: "transparent", border: "2px solid #ff4f7a", color: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 500, fontSize: "13px", whiteSpace: "nowrap" }}>View App Details</button>
                              </Link>
                            </div>
                          )}
                        </div>

                        {isMobile && (
                          <div style={{ width: "100%" }}>
                            {(() => {
                              const daysCount = Array.isArray(selectedTrackedAppData) ? selectedTrackedAppData.length : 0
                              const label = daysCount >= 7 ? "7 days data" : `${daysCount} day${daysCount === 1 ? "" : "s"}`
                              return (
                                <div style={{ marginBottom: 6 }}>
                                  <span style={{ fontSize: 10, background: "#2b2b35", color: "rgba(255,255,255,0.85)", padding: "3px 6px", borderRadius: 999, fontWeight: 600 }}>{label}</span>
                                </div>
                              )
                            })()}
                            <h3 style={{ margin: 0, color: "#fff", fontSize: "14px", lineHeight: "1.3", fontWeight: 500 }}>{selectedApp?.name || "App"}</h3>
                            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>App Score trend</div>
                          </div>
                        )}
                        <div style={{ minHeight: 220 }}>
                          {selectedTrackedAppLoading ? (
                            <Skeleton height={220} borderRadius={8} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />
                          ) : selectedTrackedAppData && selectedTrackedAppData.length > 0 ? (
                            <KTRLineChart values="asr" jsonData={selectedTrackedAppData} startDate={null} endDate={null} />
                          ) : (
                            <div style={{ color: "rgba(255,255,255,0.7)", padding: 18 }}>No graph data available.</div>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              </div>
              <div className="mt-4 w-100" style={{ background: "#21222D", border: "1px solid #353748", borderRadius: "8px", overflow: "visible" }}>
                <div className="rounded-3 p-3 p-lg-4" style={{ backgroundColor: "#21222D", overflow: "visible" }}>
                  <h2 className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ visibility: "visible", display: "flex", position: "relative", zIndex: 1 }}>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 14 }}>
                      <span style={{ fontFamily: "Rubik", fontWeight: 600, fontSize: "clamp(16px, 2vw, 22px)", color: "rgba(255, 255, 255, 1)", visibility: "visible", display: "block" }}>
                        Top Performer Apps
                      </span>
                      {/* Platform pills next to the title — desktop only; on smaller
                          screens they drop to their own row below (see the d-lg-none block). */}
                      <div className="d-none d-lg-flex">
                        <PlatformTabs
                          variant="pill"
                          showDivider={false}
                          selected={Array.isArray(topPerfFiltersRaw?.platform) ? topPerfFiltersRaw.platform : []}
                          onToggle={handleTopPerfPlatformToggle}
                        />
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button type="button" className="filter-trigger" onClick={() => setShowTopPerfFilters(true)} aria-expanded={showTopPerfFilters} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 10, padding: "7px 10px", display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", fontWeight: 600, fontSize: "14px", lineHeight: 1 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginRight: 7 }} xmlns="http://www.w3.org/2000/svg"><path d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z" fill="#fff" /></svg>
                          Filter
                          {/* `platform` is excluded here — it's the pill row above/below, not a
                              modal filter, so it shouldn't bump this badge. */}
                          {topPerfFiltersRaw && Object.keys(topPerfFiltersRaw).filter(k => k !== "platform").length > 0 && (
                            <span className="filter-badge">{Object.entries(topPerfFiltersRaw).reduce((acc, [key, v]) => { if (key === "platform") return acc; if (v == null) return acc; if (Array.isArray(v)) return acc + v.length; if (typeof v === "string" || typeof v === "number") return acc + (String(v).trim() ? 1 : 0); return acc + 1 }, 0)}</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </h2>

                  {/* Same platform pills, shown on their own row below the title on
                      mobile/tablet where there isn't room for them next to it. */}
                  <div className="d-lg-none" style={{ marginTop: "-16px", marginBottom: "10px" }}>
                    <PlatformTabs
                      variant="pill"
                      showDivider={false}
                      selected={Array.isArray(topPerfFiltersRaw?.platform) ? topPerfFiltersRaw.platform : []}
                      onToggle={handleTopPerfPlatformToggle}
                    />
                  </div>

                  <p style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "14px", lineHeight: 1.4, marginTop: "-6px", marginBottom: "14px" }}>
                    Top performing apps of the week, ranked according to KeyQuest’s App Score — a proprietary metric that blends Meta Store data with performance indicators such as user feedback, engagement, and technical stability.
                  </p>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", whiteSpace: "nowrap" }}>
                      Last updated: {topPerfLastUpdated ? new Date(topPerfLastUpdated).toLocaleString() : "—"}
                    </div>
                  </div>
                  {Object.keys(topPerfFiltersRaw || {}).filter(k => k !== "platform").length > 0 && (
                    <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {Object.entries(topPerfFiltersRaw).map(([key, value]) => {
                        if (key === "platform") return null
                        if (!value || (Array.isArray(value) && value.length === 0)) return null
                        const displayValue = Array.isArray(value) ? value.join(', ') : value
                        return (
                          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                            <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                            <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayValue}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                handleRemoveTopPerfFilter(key)
                              }}
                              aria-label={`Remove filter ${key}`}
                              style={{
                                marginLeft: 4,
                                background: 'transparent',
                                border: 'none',
                                color: '#F6255A',
                                cursor: 'pointer',
                                fontSize: 12,
                                lineHeight: 0.9,
                              }}
                            >
                              ×
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                  {showTopPerfFilters && (
                    <FilterPanel open={showTopPerfFilters} hidePlatformSection sections={filterSections.length > 0 ? filterSections : undefined} initialValues={topPerfFiltersRaw} onClose={() => setShowTopPerfFilters(false)} onApply={sel => {
                      const apiFilters = {}
                      if (sel) {
                        const normalize = v => Array.isArray(v) ? v.join(",") : v
                        if (sel.price) apiFilters.price = normalize(sel.price)
                        if (sel.rating) apiFilters.rating = normalize(sel.rating)
                        if (sel.revenue) apiFilters.revenue = normalize(sel.revenue)
                        if (sel.release_date) apiFilters.release_date = normalize(sel.release_date)
                        if (sel.genre) apiFilters.genre = normalize(sel.genre)
                        if (sel.platform) apiFilters.platform = normalize(sel.platform)
                        if (sel.bundle) apiFilters.bundle = normalize(sel.bundle)
                        if (sel.number_of_players) apiFilters.number_of_players = normalize(sel.number_of_players)
                        if (sel.sort_by || sel.sort) apiFilters.sort_by = sel.sort_by || sel.sort
                      }
                      Object.keys(sessionStorage).forEach(key => { if (key && key.startsWith("topPerformance")) sessionStorage.removeItem(key) })
                      setTopPerfFilters(apiFilters)
                      setTopPerfFiltersRaw(sel || {})
                      try { sessionStorage.setItem('dashboard_topPerfFilters', JSON.stringify(apiFilters || {})); sessionStorage.setItem('dashboard_topPerfFiltersRaw', JSON.stringify(sel || {})); } catch (e) { }
                      setTopPerfPage(1)
                      setShowTopPerfFilters(false)
                    }} />
                  )}
                  <Row className="trending_slider">
                    {topPerfLoading ? (
                      <div style={{ paddingLeft: isMobile ? "0" : `${cardGap}px`, paddingRight: isMobile ? "0" : `${cardGapHalf}px` }}>
                        <div className="d-flex gap-3" style={{ gap: isMobile ? "12px" : `${cardGap}px` }}>
                          {Array.from({ length: topPerfPerPage }).map((_, idx) => {
                            const cardDims = isMobile ? { maxWidth: "220px", minWidth: "190px", height: "225px" } : { maxWidth: "160px", minWidth: "140px", height: "130px" }
                            return (
                              <div key={idx} style={{ minWidth: cardDims.minWidth, maxWidth: cardDims.maxWidth }}>
                                <Skeleton height={cardDims.height} borderRadius={8} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />
                                {isMobile && <Skeleton width="70%" height={16} style={{ marginTop: "12px" }} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ) : topPerformanceApps && topPerformanceApps.length > 0 ? (
                      <div style={{ paddingLeft: isMobile ? "0" : `${cardGap}px`, paddingRight: isMobile ? "0" : `${cardGapHalf}px` }}>
                        <CarouselComponent games={topCurrent} defaultOpenGraph={"app_score"} disableLink={true} onCardClick={handleTopPerfClick} selectedAppId={selectedTopAppId} cardDimsOverride={{ mobile: { maxWidth: '220px', minWidth: '190px', aspectRatio: '4 / 3', padding: '10px' } }} slidesToShowOverride={isMobile ? 1 : undefined} fallbackPlatformId={Array.isArray(topPerfFiltersRaw?.platform) && topPerfFiltersRaw.platform.length === 1 ? topPerfFiltersRaw.platform[0] : null} />
                        {!topPerfLoading && topTotalPages > 1 && (
                          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                            <button type="button" onClick={() => setTopPerfPage(p => { const next = Math.max(1, p - 1); trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PAGINATED, { section: "top_performer", page: next }); return next })} disabled={topPageSafe <= 1} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.06)", color: topPageSafe <= 1 ? "rgba(255,255,255,0.35)" : "#fff", padding: "4px 8px", borderRadius: 4, fontSize: "12px", cursor: topPageSafe <= 1 ? "not-allowed" : "pointer" }}>Prev</button>

                            {getPageNumbers(topPageSafe, topTotalPages).map((page, index) => {
                              if (page === "...") return (<span key={`dots-${index}`} style={{ color: "rgba(255,255,255,0.6)", padding: "6px 8px" }}>...</span>)
                              return (
                                <button key={`tp-${page}`} type="button" onClick={() => { const next = Math.min(Math.max(1, page), topTotalPages); trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PAGINATED, { section: "top_performer", page: next }); setTopPerfPage(next) }} style={{ minWidth: 28, height: 28, borderRadius: 4, border: "1px solid rgba(255,255,255,0.06)", background: topPageSafe === page ? "#ff2f6d" : "transparent", color: topPageSafe === page ? "#fff" : "rgba(255,255,255,0.8)", fontWeight: 600, fontSize: "12px", cursor: "pointer" }}>{page}</button>
                              )
                            })}
                            <button type="button" onClick={() => setTopPerfPage(p => { const next = Math.min(topTotalPages, p + 1); trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_PAGINATED, { section: "top_performer", page: next }); return next })} disabled={topPageSafe >= topTotalPages} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.06)", color: topPageSafe >= topTotalPages ? "rgba(255,255,255,0.35)" : "#fff", padding: "4px 8px", borderRadius: 4, fontSize: "12px", cursor: topPageSafe >= topTotalPages ? "not-allowed" : "pointer" }}>Next</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ padding: 18, color: "rgba(255,255,255,0.8)" }}>No apps available</div>
                    )}
                  </Row>
                  {selectedTopAppId &&
                    (() => {
                      const selectedApp =
                        fullTop?.find(a => a.item_id === selectedTopAppId) || null
                      return (
                        <div
                          style={{
                            marginTop: 18,
                            padding: 12,
                            borderRadius: 8,
                            background: "#18181f",
                            border: "1px solid #2f2f3a",
                          }}
                        >
                          <div
                            className="d-flex gap-3 mb-3"
                            style={{
                              flexDirection: isMobile ? "column" : "row",
                              alignItems: isMobile ? "flex-start" : "center",
                              justifyContent: isMobile
                                ? "flex-start"
                                : "space-between",
                              gap: isMobile ? 12 : undefined,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: 12,
                                alignItems: "center",
                                width: isMobile ? "100%" : "auto",
                                justifyContent: isMobile
                                  ? "space-between"
                                  : "flex-start",
                                flex: isMobile ? "none" : 1,
                              }}
                            >
                              <div
                                style={{
                                  width: isMobile ? 56 : 72,
                                  height: isMobile ? 56 : 72,
                                  borderRadius: 8,
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                <img
                                  src={selectedApp?.image_url || ""}
                                  alt="app"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>

                              {!isMobile && (
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {(() => {
                                    const daysCount = Array.isArray(
                                      selectedTopAppData,
                                    )
                                      ? selectedTopAppData.length
                                      : 0
                                    const label =
                                      daysCount >= 7
                                        ? "7 days data"
                                        : `${daysCount} day${daysCount === 1 ? "" : "s"}`
                                    return (
                                      <div style={{ marginBottom: 6 }}>
                                        <span
                                          style={{
                                            fontSize: 12,
                                            background: "#2b2b35",
                                            color: "rgba(255,255,255,0.85)",
                                            padding: "4px 8px",
                                            borderRadius: 999,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {label}
                                        </span>
                                      </div>
                                    )
                                  })()}

                                  <h3 style={{ margin: 0, color: "#fff", fontSize: '18px', fontWeight: 500 }}>
                                    {selectedApp?.name || "App"}
                                  </h3>
                                  <div
                                    style={{
                                      color: "rgba(255,255,255,0.7)",
                                      fontSize: 12,
                                    }}
                                  >
                                    App Score trend
                                  </div>
                                </div>
                              )}

                              {isMobile && (
                                <div
                                  style={{
                                    flexShrink: 0,
                                    alignSelf: "flex-start",
                                  }}
                                >
                                  <Link
                                    to={`/xr/app/${generateSlug(selectedApp?.name || "")}?id=${selectedTopAppId}`}
                                    state={{
                                      app_id: selectedTopAppId,
                                      data: selectedApp,
                                      openGraph: "app_score",
                                    }}
                                    style={{ textDecoration: "none" }}
                                  >
                                    <button
                                      type="button"
                                      style={{
                                        background: "transparent",
                                        border: "1px solid #ff4f7a",
                                        color: "#fff",
                                        borderRadius: 8,
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      View App Details
                                    </button>
                                  </Link>
                                </div>
                              )}
                            </div>

                            {!isMobile && (
                              <div style={{ flexShrink: 0, alignSelf: "center" }}>
                                <Link
                                  to={`/xr/app/${generateSlug(selectedApp?.name || "")}?id=${selectedTopAppId}`}
                                  state={{
                                    app_id: selectedTopAppId,
                                    data: selectedApp,
                                    openGraph: "app_score",
                                  }}
                                  style={{ textDecoration: "none" }}
                                >
                                  <button
                                    type="button"
                                    style={{
                                      background: "transparent",
                                      border: "2px solid #ff4f7a",
                                      color: "#fff",
                                      borderRadius: 8,
                                      padding: "6px 10px",
                                      cursor: "pointer",
                                      fontWeight: 500,
                                      fontSize: "13px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    View App Details
                                  </button>
                                </Link>
                              </div>
                            )}
                          </div>

                          {isMobile && (
                            <div style={{ width: "100%" }}>
                              {(() => {
                                const daysCount = Array.isArray(selectedTopAppData)
                                  ? selectedTopAppData.length
                                  : 0
                                const label =
                                  daysCount >= 7
                                    ? "7 days data"
                                    : `${daysCount} day${daysCount === 1 ? "" : "s"}`
                                return (
                                  <div style={{ marginBottom: 6 }}>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        background: "#2b2b35",
                                        color: "rgba(255,255,255,0.85)",
                                        padding: "3px 6px",
                                        borderRadius: 999,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                )
                              })()}

                              <h3 style={{ margin: 0, color: "#fff", fontSize: "14px", lineHeight: "1.3", fontWeight: 500 }}>
                                {selectedApp?.name || "App"}
                              </h3>
                              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>App Score trend</div>
                            </div>
                          )}

                          <div style={{ minHeight: 220 }}>
                            {selectedTopAppLoading ? (
                              <Skeleton height={220} borderRadius={8} baseColor="#171821" highlightColor="#2a2b37" enableAnimation={true} />
                            ) : selectedTopAppData && selectedTopAppData.length > 0 ? (
                              <KTRLineChart values="asr" jsonData={selectedTopAppData} startDate={null} endDate={null} />
                            ) : (
                              <div style={{ color: "rgba(255,255,255,0.7)", padding: 18 }}>No graph data available.</div>
                            )}
                          </div>
                        </div>
                      )
                    })()}
                </div>
              </div>
            </div>
            {showUpgradeModal && (
              <AppsTrackingPlanModal
                show={showUpgradeModal}
                handleClose={() => setShowUpgradeModal(false)}
                title="Upgrade your plan to unlock this feature"
              />
            )}
          </div>

        </Container>
      </div>
    </React.Fragment>
  )
}

export default TopPerformerOnly
